/**
 * Projects are stored data, not code: a manager can create, rename, reorder
 * and archive them. This file holds the shape, the slug rule, and the seven
 * projects seeded on first run — nothing that assumes a fixed set.
 */

import type { Ordered } from './ordering'

export type ProjectId = string

export interface Project extends Ordered {
  id: string
  name: string
  description: string
  /**
   * Whether time logged here may carry tags.
   *
   * False for the projects that are the whole of what they are — a meeting
   * is a meeting — where asking which kind, every time, is a field nobody
   * fills in. Governs new writes only: time already logged with tags keeps
   * them if this is turned off later, because the flag says what may be
   * logged from now on, not what the past should have looked like.
   */
  allowsTags: boolean
  sortOrder: number
  archived: boolean
  /** 'system' for the seeded seven. */
  createdBy: string
  createdAt: string
  updatedAt: string
}

/**
 * Seeded once, on first run, in this order.
 *
 * The ids are the slugs the hardcoded version used, so time already logged
 * against `front-end` keeps resolving after the change. Never alter an id
 * here: renaming is what the `name` field is for.
 */
export const SEED_PROJECTS: ReadonlyArray<Pick<Project, 'id' | 'name' | 'description'>> = [
  { id: 'front-end', name: 'Front End', description: 'Client-side and UI work.' },
  { id: 'back-end', name: 'Back End', description: 'Services, APIs and data.' },
  {
    id: 'project-management',
    name: 'Project Management',
    description: 'Planning, coordination and delivery.',
  },
  { id: 'meeting', name: 'Meeting', description: 'Standups, reviews and other scheduled time.' },
  { id: 'testing', name: 'Testing', description: 'Manual and automated testing.' },
  { id: 'training', name: 'Training', description: 'Courses, reading and self-study.' },
  { id: 'miscellaneous', name: 'Miscellaneous', description: "Anything that fits nowhere else." },
]

/**
 * A url-safe id derived from the name. Ids are generated once at creation and
 * never change afterwards, so a later rename leaves the slug looking stale —
 * that is fine, and far better than rewriting every row that references it.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/**
 * Display name for a stored id.
 *
 * Falls back to the id rather than to some default project: with projects
 * editable, an unrecognised id means the row predates a change, and silently
 * relabelling it as something else would misreport history.
 */
export function projectName(projects: readonly Project[], id: string): string {
  return projects.find((c) => c.id === id)?.name ?? id
}
